/**
 * DLUX Wishlist — localStorage-based product wishlist
 *
 * Data shape in localStorage key `dlux-wishlist`:
 *   JSON string array of product handles, e.g. ["rose-serum-30ml", "glow-toner"]
 *
 * Events dispatched:
 *   CustomEvent("dlux:wishlist:change") with detail { handle, action: "added"|"removed" }
 */
(function () {
  "use strict";

  var STORAGE_KEY = "dlux-wishlist";
  var ACTIVE_CLASS = "is-active";

  /* ---- storage helpers ---- */

  function getSet() {
    try {
      return new Set(JSON.parse(localStorage.getItem(STORAGE_KEY)) || []);
    } catch (_) {
      return new Set();
    }
  }

  function saveSet(set) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  }

  /* ---- translation helpers ---- */

  function t(key, fallback) {
    if (window.wishlistStrings && window.wishlistStrings[key]) return window.wishlistStrings[key];
    if (window.accessibilityStrings && window.accessibilityStrings[key]) return window.accessibilityStrings[key];
    return fallback;
  }

  /* ---- UI helpers ---- */

  function updateButtons(handle, active) {
    var selector =
      '.dlux-card-wishlist-btn[data-product-handle="' + handle + '"],' +
      '.dlux-pdp-wishlist-btn[data-product-handle="' + handle + '"]';
    document.querySelectorAll(selector).forEach(function (btn) {
      var label = btn.querySelector(".dlux-pdp-wishlist-btn__label");
      if (active) {
        btn.classList.add(ACTIVE_CLASS);
        btn.setAttribute("aria-label", t("wishlistRemove", "Remove from wishlist"));
        if (label) label.textContent = label.getAttribute("data-label-remove") || t("wishlistRemove", "Remove from wishlist");
      } else {
        btn.classList.remove(ACTIVE_CLASS);
        btn.setAttribute("aria-label", t("wishlistAdd", "Add to wishlist"));
        if (label) label.textContent = label.getAttribute("data-label-add") || t("wishlistAdd", "Add to wishlist");
      }
    });
  }

  function updateHeaderCount() {
    var count = getSet().size;
    var bubbles = document.querySelectorAll(".dlux-wishlist-count-bubble");
    bubbles.forEach(function (bubble) {
      if (count > 0) {
        bubble.removeAttribute("hidden");
        var numEl = bubble.querySelector("[aria-hidden]");
        var srEl = bubble.querySelector(".visually-hidden");
        if (numEl) numEl.textContent = count;
        if (srEl) srEl.textContent = count + (count === 1 ? " item in wishlist" : " items in wishlist");
      } else {
        bubble.setAttribute("hidden", "");
      }
    });
  }

  var toastEl = null;
  var toastTimer = null;

  function showToast(message) {
    if (!toastEl) {
      toastEl = document.createElement("div");
      toastEl.className = "dlux-wishlist-toast";
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = message;
    // force reflow so re-triggering the same message still animates
    void toastEl.offsetHeight;
    toastEl.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      toastEl.classList.remove("is-visible");
    }, 2000);
  }

  /* ---- core ---- */

  function toggle(handle) {
    var set = getSet();
    var wasActive = set.has(handle);
    var action;

    if (wasActive) {
      set.delete(handle);
      action = "removed";
    } else {
      set.add(handle);
      action = "added";
    }

    saveSet(set);
    updateButtons(handle, !wasActive);
    updateHeaderCount();

    showToast(
      action === "added"
        ? t("added", "Added to wishlist")
        : t("removed", "Removed from wishlist")
    );

    document.dispatchEvent(
      new CustomEvent("dlux:wishlist:change", { detail: { handle: handle, action: action } })
    );
  }

  /* ---- init on DOM ready ---- */

  function init() {
    var set = getSet();
    set.forEach(function (handle) {
      updateButtons(handle, true);
    });
    updateHeaderCount();
  }

  /* ---- event delegation ---- */

  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".dlux-card-wishlist-btn, .dlux-pdp-wishlist-btn");
    if (!btn) return;
    e.preventDefault();
    var handle = btn.getAttribute("data-product-handle");
    if (handle) toggle(handle);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* expose for programmatic access */
  function clear() {
    var set = getSet();
    var handles = [...set];
    set.clear();
    saveSet(set);
    handles.forEach(function (handle) {
      updateButtons(handle, false);
    });
    updateHeaderCount();
    document.dispatchEvent(
      new CustomEvent("dlux:wishlist:change", { detail: { handle: null, action: "cleared" } })
    );
  }

  window.DLUXWishlist = { getSet: getSet, toggle: toggle, clear: clear, init: init, updateHeaderCount: updateHeaderCount };
})();
